import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronUp, ChevronDown, Hand } from "lucide-react";

interface ScrollableContainerProps {
	children: ReactNode;
	className?: string;
	/** Show initial hint that fades away */
	showInitialHint?: boolean;
	/** Duration in ms to show hint before fading */
	hintDuration?: number;
}

/**
 * A container that wraps content and shows visual scroll indicators
 * when there's more content to scroll vertically
 */
export default function ScrollableContainer({
	children,
	className = "",
	showInitialHint = true,
	hintDuration = 3000,
}: ScrollableContainerProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [canScrollUp, setCanScrollUp] = useState(false);
	const [canScrollDown, setCanScrollDown] = useState(false);
	const [showHint, setShowHint] = useState(showInitialHint);

	// Check scroll position to update indicators
	const checkScroll = () => {
		const container = containerRef.current;
		if (!container) return;

		const { scrollTop, scrollHeight, clientHeight } = container;
		setCanScrollUp(scrollTop > 10);
		setCanScrollDown(scrollTop < scrollHeight - clientHeight - 10);
	};

	// Set up scroll listener and initial check
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		// Initial check after a brief delay to ensure content is loaded
		const initialCheckTimeout = setTimeout(checkScroll, 500);

		// Add scroll listener
		container.addEventListener("scroll", checkScroll);

		// Recheck on window resize
		window.addEventListener("resize", checkScroll);

		return () => {
			clearTimeout(initialCheckTimeout);
			container.removeEventListener("scroll", checkScroll);
			window.removeEventListener("resize", checkScroll);
		};
	}, []);

	// Handle hint fade out
	useEffect(() => {
		if (!showInitialHint) return;

		const timeout = setTimeout(() => {
			setShowHint(false);
		}, hintDuration);

		return () => clearTimeout(timeout);
	}, [showInitialHint, hintDuration]);

	return (
		<div className={`relative w-full h-full ${className}`}>
			{/* Initial hint overlay */}
			{showHint && (
				<div className="absolute right-0 top-1/2 -translate-y-1/2 z-30 pointer-events-none animate-in fade-in duration-500">
					<div className="bg-black/70 text-white px-1 py-6 rounded-lg shadow-xl flex flex-col items-center gap-3 animate-out fade-out duration-2000 delay-2000">
						<ChevronUp className="w-5 h-5" />
						<Hand className="w-5 h-5 opacity-70" />
						<ChevronDown className="w-5 h-5" />
					</div>
				</div>
			)}

			{/* Top scroll indicator */}
			{canScrollUp && (
				<div className="absolute left-0 top-0 w-14 h-24 z-20 pointer-events-none bg-gradient-to-b from-gray-400/25 via-gray-300/15 to-transparent flex flex-col items-center pt-3 gap-1 animate-in fade-in slide-in-from-top duration-300">
					<div className="bg-black/70 rounded-full p-2 shadow-lg">
						<ChevronUp className="w-5 h-5 text-white" />
					</div>
					<div className="flex flex-col gap-0.5">
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-60" />
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-40" />
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-20" />
					</div>
				</div>
			)}

			{/* Bottom scroll indicator */}
			{canScrollDown && (
				<div className="absolute left-0 bottom-0 w-14 h-24 z-20 pointer-events-none bg-gradient-to-t from-gray-400/25 via-gray-300/15 to-transparent flex flex-col items-center pb-3 gap-1 animate-in fade-in slide-in-from-bottom duration-300">
					<div className="flex flex-col gap-0.5">
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-20" />
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-40" />
						<div className="w-1 h-1 bg-gray-600 rounded-full opacity-60" />
					</div>
					<div className="bg-black/70 rounded-full p-2 shadow-lg">
						<ChevronDown className="w-5 h-5 text-white" />
					</div>
				</div>
			)}

			{/* Scrollable content */}
			<div
				ref={containerRef}
				className="w-full h-full overflow-auto"
				style={{ scrollBehavior: "smooth" }}
			>
				{children}
			</div>
		</div>
	);
}
